import { useEffect, useState } from "react";
import axios from "axios";
import {
    Sun,
    Fire,
    CheckCircle,
    Trophy,
    Lightning,
    ArrowSquareOut,
    EnvelopeSimple,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Digest() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/digest/today`);
                setData(data);
            } catch (e) {
                const msg = e?.response?.data?.detail || e.message || "Failed to load";
                setErr(String(msg));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div className="min-h-screen bg-white bg-grid" data-testid="digest-page">
            <main className="p-6 lg:p-8 max-w-[1200px] mx-auto w-full">
                <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="label-kicker mb-2 flex items-center gap-2">
                            <Sun size={12} weight="bold" />
                            Morning Digest
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
                            Yesterday in numbers.
                        </h1>
                        <p className="mt-3 text-[#52525b] text-sm max-w-xl">
                            Preview only. This page will eventually ship as an automated
                            email, but today it's a quick glance for managers before the
                            team lands.
                        </p>
                    </div>
                    <span className="mono text-[11px] text-[#52525b]">
                        <EnvelopeSimple size={12} weight="bold" className="inline mr-1" />
                        UI preview · no sending yet
                    </span>
                </div>

                {loading && <div className="panel p-10 text-center text-sm text-[#52525b]">Loading…</div>}
                {err && <div className="panel p-10 text-center text-sm text-[#ff3333]" data-testid="digest-error">{err}</div>}

                {data && !err && (
                    <>
                        <section className="panel mb-6" data-testid="digest-yesterday">
                            <div className="panel-header">
                                <span className="label-kicker">Last 24 hours</span>
                                <span className="mono text-[11px] text-[#52525b]">
                                    {new Date(data.generated_at).toLocaleString()}
                                </span>
                            </div>
                            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                                <Tile Icon={Fire} label="New providers" value={data.yesterday.new_providers} color="#002FA7" testId="digest-new" />
                                <Tile Icon={Lightning} label="Contacted" value={data.yesterday.contacted} color="#000" testId="digest-contacted" />
                                <Tile Icon={CheckCircle} label="Interested" value={data.yesterday.interested} color="#002FA7" testId="digest-interested" />
                                <Tile Icon={Trophy} label="Claimed" value={data.yesterday.claimed} color="#000" testId="digest-claimed" />
                            </div>
                        </section>

                        {data.top_operators?.length > 0 && (
                            <section className="panel mb-6" data-testid="digest-top-operators">
                                <div className="panel-header">
                                    <span className="label-kicker">Top operators (yesterday)</span>
                                </div>
                                <div className="divide-y divide-[#e4e4e7]">
                                    {data.top_operators.map((op, i) => (
                                        <div
                                            key={op.operator}
                                            className="p-4 flex items-center justify-between"
                                            data-testid={`digest-operator-${op.operator}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="mono text-[10px] text-[#a1a1aa]">
                                                    {String(i + 1).padStart(2, "0")}
                                                </span>
                                                <span className="font-medium">{op.operator}</span>
                                                {i === 0 && (
                                                    <span className="mono text-[9px] px-1.5 py-0.5 bg-black text-white">
                                                        TOP
                                                    </span>
                                                )}
                                            </div>
                                            <span className="mono text-[12px] text-[#52525b]">
                                                {op.actions} actions
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {data.today_priorities_preview?.length > 0 && (
                            <section className="panel" data-testid="digest-priorities">
                                <div className="panel-header">
                                    <span className="label-kicker">Today's top 5 priorities</span>
                                </div>
                                <div className="divide-y divide-[#e4e4e7]">
                                    {data.today_priorities_preview.map((p, i) => (
                                        <div
                                            key={p.id || `${p.name || "?"}-${i}`}
                                            className="p-4 grid grid-cols-1 md:grid-cols-[40px_minmax(180px,1.2fr)_minmax(220px,1.5fr)_auto] gap-4 items-center"
                                            data-testid={`digest-priority-${i}`}
                                        >
                                            <span className="mono text-[10px] text-[#a1a1aa]">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <div>
                                                <div className="font-medium">{p.name || "—"}</div>
                                                <div className="label-kicker" style={{ fontSize: 9 }}>
                                                    {p.category} · {p.location}
                                                </div>
                                            </div>
                                            <div className="mono text-[11px] text-[#52525b]">
                                                {p.phone || ""} {p.email ? `· ${p.email}` : ""}
                                            </div>
                                            {p.website && (
                                                <a
                                                    href={p.website}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="inline-flex items-center gap-1 mono text-[11px] underline"
                                                >
                                                    website
                                                    <ArrowSquareOut size={10} />
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}

function Tile({ Icon, label, value, color, testId }) {
    return (
        <div
            className="border border-[#e4e4e7] p-3 flex flex-col gap-1"
            data-testid={testId}
        >
            <span className="label-kicker flex items-center gap-1" style={{ fontSize: 9 }}>
                <Icon size={11} weight="bold" />
                {label}
            </span>
            <span className="font-display text-3xl font-bold" style={{ color }}>
                {value ?? 0}
            </span>
        </div>
    );
}
